import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../quiz-flow/pages/Question';
import { Confetti } from '../Confetti';
import { flows } from '../flow_template';
import { Pages } from '../page_list';
import { BaseResponseCount } from '../response_counts';
import { SocialProofAvatars } from '@/components/molecules/SocialProofAvatars';
import { QuizSliderCloseButton } from './QuizSliderCloseButton';

const QuizSlider = ({
    variant,
    isOpen,
    onNext,
    mounted,
    selectedOption,
    setSelectedOption,
    onClose,
    flowVersion = 'flow_a'
}) => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);
    const [confettiPosition, setConfettiPosition] = useState({ x: 0, y: 0 });

    const flow = flows[flowVersion];
    const currentPageName = flow.pages[currentPageIndex];
    const currentPage = Pages[currentPageName];

    useEffect(() => {
        if (!isOpen) {
            setCurrentPageIndex(0);
            setSelectedOption(null);
        }
    }, [isOpen, setSelectedOption]);

    const handleAnswer = (answer, event) => {
        setSelectedOption(answer);
        setShowConfetti(true);
        setConfettiPosition({ x: event.clientX, y: event.clientY });

        setTimeout(() => {
            setShowConfetti(false);
            onNext({
                questionText: currentPage.text,
                answerPosition: currentPage.answers.findIndex(a => a.id === answer.id) + 1,
                answerText: answer.text,
                fullAnswer: answer
            });
        }, 500);
    };

    const formatResponseCount = (count) => {
        const thousands = count / 1000;
        return thousands % 1 === 0
            ? `${Math.floor(thousands)}k`
            : `${thousands.toFixed(1)}k`;
    };

    const sliderVariants = {
        hidden: { y: '100%', opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { type: 'spring', damping: 30, stiffness: 300 } },
        exit: { y: '100%', opacity: 0, transition: { duration: 0.3 } }
    };

    return (
        <AnimatePresence>
            {isOpen && mounted && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
                        onClick={onClose}
                    />

                    <motion.div
                        className="fixed inset-x-0 bottom-0 z-50 rounded-t-[32px] shadow-lg"
                        style={{
                            background: "linear-gradient(80deg, rgba(240, 30, 111, 0.05) 12.92%, rgba(248, 94, 74, 0.05) 94.79%), #FFF"
                        }}
                        variants={sliderVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <QuizSliderCloseButton onClose={onClose} />

                        <div className="px-6 pt-6">
                            <h1 className="text-2xl font-bold mb-6 text-black text-center">
                                Find gluten-free spots<br />
                                tailored to your needs
                            </h1>
                            <div className="w-full h-[1px] bg-black/20" />
                        </div>

                        <Question
                            data={currentPage}
                            onAnswer={handleAnswer}
                            flowVersion={flowVersion}
                            previousAnswer={selectedOption}
                            setClickPosition={setConfettiPosition}
                            setShowConfetti={setShowConfetti}
                            skipAnswerAnimation={true}
                            isInSlider={true}
                        />

                        <div className="px-6 pb-6">
                            <div className="flex items-center justify-center gap-1 mt-4">
                                <SocialProofAvatars
                                    count={3}
                                    avatarsBorderColor="white"
                                />
                                <span className="text-gray-600 text-sm ml-1">
                                    {formatResponseCount(BaseResponseCount)} responses
                                </span>
                            </div>
                        </div>

                        <Confetti
                            isActive={showConfetti}
                            clickPosition={confettiPosition}
                            emojis={selectedOption?.emojis || ['🎉', '🎊', '✨']}
                        />
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default QuizSlider;